import { IRangeOverviewClient } from './IRangeOverviewClient';
import { Range, RangeListResponse, RangeTile, RangeScopeType, RangeStatus, ReadinessStatus } from './rangeOverviewTypes';
import { ranges as seedRanges } from '../../../../mocks/data/rangesData';
import { menus } from '../../../../mocks/data/menusData';
import { MenuStatus } from '../menus/menusTypes';
import { mockRecipesV2 } from '../../../../mocks/data/recipeV2Data';
import { AllergenStatus } from '../recipeV2/recipeV2Types';
import { duplicateItemGroups, unusedItems, missingDataItems, priceAnomalies } from '../../../../mocks/data/itemRationalisationData';

/** In-memory mocked client for the Range Overview control tower. Ranges are demo-only mock
 * data (src/mocks/data/rangesData.ts), but every tile is computed live from the same
 * Menus/Recipes/Item Rationalisation data the rest of Range Planning already uses, so the
 * numbers stay consistent with what those screens show. */
export class RangeOverviewClientMocked implements IRangeOverviewClient {
  private ranges: Range[] = [...seedRanges];

  async getRanges(scopeType: RangeScopeType, scopeValue: string | string[], search: string): Promise<RangeListResponse> {
    const term = search.trim().toLowerCase();
    const filtered = this.ranges.filter((range) => {
      const matchesSearch = !term
        || range.name.toLowerCase().includes(term)
        || range.customerName.toLowerCase().includes(term)
        || range.clientAccount.toLowerCase().includes(term);
      if (!matchesSearch) return false;

      switch (scopeType) {
        case 'WholeEstate':
          return true;
        case 'Site':
          return !scopeValue || range.sites.some((s) => s.siteName === scopeValue);
        case 'MultiSite': {
          const values = Array.isArray(scopeValue) ? scopeValue : [];
          return values.length === 0 || range.sites.some((s) => values.includes(s.siteName));
        }
        case 'ClientAccount':
          return !scopeValue || range.clientAccount === scopeValue;
        case 'SiteGroup':
          return !scopeValue || range.siteGroup === scopeValue;
        default:
          return true;
      }
    });

    return { data: filtered, totalCount: filtered.length };
  }

  async getScopeOptions(): Promise<{ sites: string[]; clientAccounts: string[]; siteGroups: string[] }> {
    const sites = Array.from(new Set(this.ranges.flatMap((o) => o.sites.map((s) => s.siteName)))).sort();
    const clientAccounts = Array.from(new Set(this.ranges.map((o) => o.clientAccount))).sort();
    const siteGroups = Array.from(new Set(this.ranges.map((o) => o.siteGroup))).sort();
    return { sites, clientAccounts, siteGroups };
  }

  async getTiles(): Promise<RangeTile[]> {
    const activeMenus = menus.filter((m) => m.status === MenuStatus.Live).length;
    const menusAwaitingApproval = this.ranges.filter((o) => o.status === RangeStatus.InReview).length;

    // lazy: cost-movement flagged with the same deterministic per-recipe-id variance heuristic
    // used in CostingTab's Price Confidence panel (no real price-history feed exists yet) -
    // upgrade path: source both from real supplier price-history once available.
    const recipesWithCostMovement = mockRecipesV2.filter((r) => {
      const contracted = r.totalCost * (0.94 + (r.id % 5) * 0.01);
      return Math.abs(r.totalCost - contracted) / Math.max(contracted, 0.01) > 0.03;
    }).length;

    const recipesWithAllergenWarnings = mockRecipesV2.filter((r) =>
      r.allergenSummary.some((a) => a.status === AllergenStatus.MayContain)).length;

    const itemsRequiringRationalisation = duplicateItemGroups.length
      + unusedItems.filter((i) => !i.usedInRecipe).length
      + missingDataItems.length
      + priceAnomalies.length;

    const menusMissingCarbonData = menus.filter((menu) => {
      const recipeIds = new Set<number>();
      menu.courses.forEach((c) => c.dishes.forEach((d) => recipeIds.add(d.recipeId)));
      (menu.dayStructure ?? []).forEach((day) => day.courses.forEach((c) => c.dishes.forEach((d) => recipeIds.add(d.recipeId))));
      return Array.from(recipeIds).some((id) => mockRecipesV2.find((r) => r.id === id)?.carbonScore == null);
    }).length;

    const publishedSites = new Set(this.ranges.filter((o) => o.lastPublished).flatMap((o) => o.sites.map((s) => s.siteName)));
    const allSites = new Set(this.ranges.flatMap((o) => o.sites.map((s) => s.siteName)));
    const sitesWithoutPublishedRange = Array.from(allSites).filter((site) => !publishedSites.has(site)).length;

    const upcomingMenuChanges = this.ranges.filter((o) => o.status === RangeStatus.Scheduled || o.status === RangeStatus.InReview).length;

    return [
      { key: 'activeMenus', labelKey: 'tiles.activeMenus', defaultLabel: 'Active menus', value: activeMenus, href: '/range/menus' },
      { key: 'menusAwaitingApproval', labelKey: 'tiles.menusAwaitingApproval', defaultLabel: 'Menus awaiting approval', value: menusAwaitingApproval, href: '/range/menus' },
      { key: 'recipesWithCostMovement', labelKey: 'tiles.recipesWithCostMovement', defaultLabel: 'Recipes with cost movement', value: recipesWithCostMovement, href: '/range/recipes' },
      { key: 'recipesWithAllergenWarnings', labelKey: 'tiles.recipesWithAllergenWarnings', defaultLabel: 'Recipes with allergen warnings', value: recipesWithAllergenWarnings, href: '/range/recipes' },
      { key: 'itemsRequiringRationalisation', labelKey: 'tiles.itemsRequiringRationalisation', defaultLabel: 'Items requiring rationalisation', value: itemsRequiringRationalisation, href: '/range-planning/item-rationalisation' },
      { key: 'menusMissingCarbonData', labelKey: 'tiles.menusMissingCarbonData', defaultLabel: 'Menus missing carbon data', value: menusMissingCarbonData, href: '/range/menus' },
      { key: 'sitesWithoutPublishedRange', labelKey: 'tiles.sitesWithoutPublishedRange', defaultLabel: 'Sites without a published range', value: sitesWithoutPublishedRange, href: '#' },
      { key: 'upcomingMenuChanges', labelKey: 'tiles.upcomingMenuChanges', defaultLabel: 'Upcoming menu changes', value: upcomingMenuChanges, href: '#' },
    ];
  }

  async duplicateRange(rangeId: number): Promise<Range> {
    const source = this.ranges.find((o) => o.id === rangeId);
    if (!source) throw new Error('Range not found');
    const newRange: Range = {
      ...source,
      id: Math.max(...this.ranges.map((o) => o.id)) + 1,
      name: `${source.name} (Copy)`,
      status: RangeStatus.Draft,
      lastPublished: null,
    };
    this.ranges = [...this.ranges, newRange];
    return newRange;
  }

  async publishRange(rangeId: number): Promise<Range> {
    const range = this.ranges.find((o) => o.id === rangeId);
    if (!range) throw new Error('Range not found');
    if (range.costStatus === ReadinessStatus.Blocked || range.allergenStatus === ReadinessStatus.Blocked || range.carbonStatus === ReadinessStatus.Blocked) {
      throw new Error('Range cannot be published while cost, allergen or carbon readiness is Blocked.');
    }
    range.status = RangeStatus.Live;
    range.lastPublished = new Date().toISOString().slice(0, 10);
    return range;
  }

  async createRange(input: { name: string; customerName: string; clientAccount: string; siteGroup: string; siteNames: string[] }): Promise<Range> {
    const newRange: Range = {
      id: Math.max(...this.ranges.map((o) => o.id)) + 1,
      name: input.name,
      menuId: null,
      customerName: input.customerName,
      clientAccount: input.clientAccount,
      siteGroup: input.siteGroup,
      sites: input.siteNames.map((siteName) => ({ siteName, region: 'Unassigned' })),
      servicePeriod: '-',
      activeFrom: new Date().toISOString().slice(0, 10),
      activeTo: null,
      status: RangeStatus.Draft,
      costStatus: ReadinessStatus.Attention,
      costStatusNote: 'New range - not yet costed.',
      allergenStatus: ReadinessStatus.Attention,
      allergenStatusNote: 'New range - allergens not yet reviewed.',
      carbonStatus: ReadinessStatus.Attention,
      carbonStatusNote: 'New range - carbon not yet reviewed.',
      lastPublished: null,
    };
    this.ranges = [...this.ranges, newRange];
    return newRange;
  }
}
